import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  IconButton,
  TextField,
  Divider,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useApp } from '../context/AppContext';
import { useThemeColors } from '../hooks/useThemeColors';
import { anos, meses } from '../data/mockData';
import { Checkbox, FormGroup, FormControlLabel, OutlinedInput } from '@mui/material';

function Apresentacao() {
  const { corretores, calcularVGVTotal } = useApp();
  const colors = useThemeColors();
  const [anoSelecionado, setAnoSelecionado] = useState(new Date().getFullYear());
  const [mesesSelecionados, setMesesSelecionados] = useState(meses); // Por padrão, todos os meses
  const [apresentacaoAtiva, setApresentacaoAtiva] = useState(false);
  const [telaAtual, setTelaAtual] = useState(1); // 1: Tela inicial, 2: Valor, 3: Pódio e corretor
  const [corretorAtualIndex, setCorretorAtualIndex] = useState(0); // Índice do corretor atual (0, 1 ou 2)
  const [tipoRankingAtual, setTipoRankingAtual] = useState('agenciamentos'); // 'agenciamentos', 'contratos', 'vgv'
  const [imagemProgressoErro, setImagemProgressoErro] = useState(false);
  
  // Load custom photos from localStorage
  const getCustomPhotos = () => {
    try {
      const stored = localStorage.getItem(`apresentacao_fotos_${anoSelecionado}`);
      return stored ? JSON.parse(stored) : {};
    } catch {
      return {};
    }
  };

  const [customPhotos, setCustomPhotos] = useState(getCustomPhotos());

  // Load custom texts from localStorage
  const getCustomTexts = () => {
    try {
      const stored = localStorage.getItem('apresentacao_textos');
      return stored ? JSON.parse(stored) : {
        tituloPrincipal: 'AGRADECEMOS!',
        subtitulo: 'A todos os corretores',
        mensagem: 'Pelo excelente trabalho e dedicação em',
      };
    } catch {
      return {
        tituloPrincipal: 'AGRADECEMOS!',
        subtitulo: 'A todos os corretores',
        mensagem: 'Pelo excelente trabalho e dedicação em',
      };
    }
  };

  const [customTexts, setCustomTexts] = useState(getCustomTexts());

  // Save custom texts to localStorage
  const saveCustomTexts = (texts) => {
    localStorage.setItem('apresentacao_textos', JSON.stringify(texts));
    setCustomTexts(texts);
  };

  // Reload custom photos when year changes
  useEffect(() => {
    setCustomPhotos(getCustomPhotos());
  }, [anoSelecionado]);

  // Save custom photos to localStorage
  const saveCustomPhotos = (photos) => {
    localStorage.setItem(`apresentacao_fotos_${anoSelecionado}`, JSON.stringify(photos));
    setCustomPhotos(photos);
  };

  // Handle photo upload
  const handlePhotoUpload = (corretorId, event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate image file
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione um arquivo de imagem');
      return;
    }

    // Read file as data URL
    const reader = new FileReader();
    reader.onload = (e) => {
      const newPhotos = { ...customPhotos, [corretorId]: e.target.result };
      saveCustomPhotos(newPhotos);
    };
    reader.readAsDataURL(file);
  };

  // Handle photo removal
  const handlePhotoRemove = (corretorId) => {
    const newPhotos = { ...customPhotos };
    delete newPhotos[corretorId];
    saveCustomPhotos(newPhotos);
  };

  // Get photo for corretor (custom or profile)
  const getCorretorPhoto = (corretor) => {
    return customPhotos[corretor.id] || corretor.foto || undefined;
  };

  const formatarMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(valor);
  };

  // Calculate VGV total for the selected period (meses)
  const calcularVGVPeriodo = (corretor, ano, mesesSelecionados) => {
    if (!corretor.vendas || !corretor.vendas[ano]) return 0;
    if (!mesesSelecionados || mesesSelecionados.length === 0) return 0;
    
    return mesesSelecionados.reduce((total, mes) => {
      const vendaMes = corretor.vendas[ano]?.[mes];
      if (!vendaMes) return total;
      const valor = typeof vendaMes === 'object' ? (vendaMes.valor || 0) : (vendaMes || 0);
      return total + valor;
    }, 0);
  };

  // Calculate total agenciamentos for the selected period (meses)
  const calcularAgenciamentosPeriodo = (corretor, ano, mesesSelecionados) => {
    if (!corretor.vendas || !corretor.vendas[ano]) return 0;
    if (!mesesSelecionados || mesesSelecionados.length === 0) return 0;
    
    return mesesSelecionados.reduce((total, mes) => {
      const vendaMes = corretor.vendas[ano]?.[mes];
      if (!vendaMes || typeof vendaMes !== 'object') return total;
      const agenciamento = vendaMes.agenciamento || 0;
      return total + parseInt(agenciamento, 10);
    }, 0);
  };

  // Calculate total numero_contratos for the selected period (meses)
  const calcularContratosPeriodo = (corretor, ano, mesesSelecionados) => {
    if (!corretor.vendas || !corretor.vendas[ano]) return 0;
    if (!mesesSelecionados || mesesSelecionados.length === 0) return 0;
    
    return mesesSelecionados.reduce((total, mes) => {
      const vendaMes = corretor.vendas[ano]?.[mes];
      if (!vendaMes || typeof vendaMes !== 'object') return total;
      // Check both snake_case and camelCase formats
      const numeroContratos = vendaMes.numero_contratos || vendaMes.numeroContratos || 0;
      return total + parseInt(numeroContratos, 10);
    }, 0);
  };

  // Filtrar apenas corretores ativos
  const corretoresAtivos = corretores.filter(c => c.ativo);

  // Calculate ranking based on tipo (agenciamentos, contratos, vgv)
  const rankingCorretores = useMemo(() => {
    return corretoresAtivos
      .map(corretor => {
        let valor = 0;
        if (tipoRankingAtual === 'vgv') {
          valor = calcularVGVPeriodo(corretor, anoSelecionado, mesesSelecionados);
        } else if (tipoRankingAtual === 'agenciamentos') {
          valor = calcularAgenciamentosPeriodo(corretor, anoSelecionado, mesesSelecionados);
        } else if (tipoRankingAtual === 'contratos') {
          valor = calcularContratosPeriodo(corretor, anoSelecionado, mesesSelecionados);
        }
        return {
          ...corretor,
          valorRanking: valor,
          vgvAno: calcularVGVPeriodo(corretor, anoSelecionado, mesesSelecionados), // Keep for display
          agenciamentosAno: calcularAgenciamentosPeriodo(corretor, anoSelecionado, mesesSelecionados), // Keep for display
          contratosAno: calcularContratosPeriodo(corretor, anoSelecionado, mesesSelecionados), // Keep for display
        };
      })
      .filter(c => c.valorRanking > 0)
      .sort((a, b) => b.valorRanking - a.valorRanking)
      .map((corretor, index) => ({
        ...corretor,
        posicao: index + 1,
      }));
  }, [corretoresAtivos, anoSelecionado, mesesSelecionados, tipoRankingAtual]);

  // Get top 3 corretores (ordenados: 1°, 2°, 3° para exibição)
  const top3Corretores = useMemo(() => {
    const top3 = rankingCorretores.filter(c => c.posicao <= 3).slice(0, 3);
    // Ordem: 1° lugar primeiro, depois 2°, depois 3°
    return top3;
  }, [rankingCorretores]);

  // Calculate top 3 for each ranking type (for preview)
  const top3Agenciamentos = useMemo(() => {
    const ranking = corretoresAtivos
      .map(corretor => ({
        ...corretor,
        valorRanking: calcularAgenciamentosPeriodo(corretor, anoSelecionado, mesesSelecionados),
        vgvAno: calcularVGVPeriodo(corretor, anoSelecionado, mesesSelecionados),
        agenciamentosAno: calcularAgenciamentosPeriodo(corretor, anoSelecionado, mesesSelecionados),
        contratosAno: calcularContratosPeriodo(corretor, anoSelecionado, mesesSelecionados),
      }))
      .filter(c => c.valorRanking > 0)
      .sort((a, b) => b.valorRanking - a.valorRanking)
      .map((corretor, index) => ({
        ...corretor,
        posicao: index + 1,
      }));
    const top3 = ranking.filter(c => c.posicao <= 3).slice(0, 3);
    return top3;
  }, [corretoresAtivos, anoSelecionado, mesesSelecionados]);

  const top3Contratos = useMemo(() => {
    const ranking = corretoresAtivos
      .map(corretor => ({
        ...corretor,
        valorRanking: calcularContratosPeriodo(corretor, anoSelecionado, mesesSelecionados),
        vgvAno: calcularVGVPeriodo(corretor, anoSelecionado, mesesSelecionados),
        agenciamentosAno: calcularAgenciamentosPeriodo(corretor, anoSelecionado, mesesSelecionados),
        contratosAno: calcularContratosPeriodo(corretor, anoSelecionado, mesesSelecionados),
      }))
      .filter(c => c.valorRanking > 0)
      .sort((a, b) => b.valorRanking - a.valorRanking)
      .map((corretor, index) => ({
        ...corretor,
        posicao: index + 1,
      }));
    const top3 = ranking.filter(c => c.posicao <= 3).slice(0, 3);
    return top3;
  }, [corretoresAtivos, anoSelecionado, mesesSelecionados]);

  const top3VGV = useMemo(() => {
    const ranking = corretoresAtivos
      .map(corretor => ({
        ...corretor,
        valorRanking: calcularVGVPeriodo(corretor, anoSelecionado, mesesSelecionados),
        vgvAno: calcularVGVPeriodo(corretor, anoSelecionado, mesesSelecionados),
        agenciamentosAno: calcularAgenciamentosPeriodo(corretor, anoSelecionado, mesesSelecionados),
        contratosAno: calcularContratosPeriodo(corretor, anoSelecionado, mesesSelecionados),
      }))
      .filter(c => c.valorRanking > 0)
      .sort((a, b) => b.valorRanking - a.valorRanking)
      .map((corretor, index) => ({
        ...corretor,
        posicao: index + 1,
      }));
    const top3 = ranking.filter(c => c.posicao <= 3).slice(0, 3);
    return top3;
  }, [corretoresAtivos, anoSelecionado, mesesSelecionados]);

  // Get current corretor data for presentation
  const corretorData = useMemo(() => {
    if (top3Corretores.length === 0) return null;
    const corretor = top3Corretores[corretorAtualIndex] || top3Corretores[0];
    return {
      ...corretor,
      fotoApresentacao: getCorretorPhoto(corretor),
    };
  }, [top3Corretores, corretorAtualIndex, customPhotos]);

  // Get title based on tipo ranking
  const getTituloRanking = () => {
    switch (tipoRankingAtual) {
      case 'agenciamentos':
        return 'Top Agenciamentos';
      case 'contratos':
        return 'Top Contratos';
      case 'vgv':
        return 'Top VGV';
      default:
        return 'Top VGV';
    }
  };

  // Get value to display based on tipo ranking
  const getValorExibicao = (corretor) => {
    if (!corretor) return 0;
    switch (tipoRankingAtual) {
      case 'agenciamentos':
        return corretor.agenciamentosAno || 0;
      case 'contratos':
        return corretor.contratosAno || 0;
      case 'vgv':
        return corretor.vgvAno || 0;
      default:
        return corretor.vgvAno || 0;
    }
  };

  // Format value based on tipo ranking
  const formatarValorRanking = (valor) => {
    if (tipoRankingAtual === 'vgv') {
      return formatarMoeda(valor);
    } else {
      // For agenciamentos and contratos, show as number
      return valor.toLocaleString('pt-BR');
    }
  };

  // Get podium image and border color based on position
  const getPodiumConfig = (posicao) => {
    switch (posicao) {
      case 1:
        return {
          imagem: '/podiumdouradoo.png',
          corBorda: '#DAA520', // douradooo
          nomeCor: 'douradooo',
        };
      case 2:
        return {
          imagem: '/prataa.png', // Prata para 2° lugar
          corBorda: '#C0C0C0', // Prata
          nomeCor: 'Prata',
        };
      case 3:
        return {
          imagem: '/bronze2.png', // Bronze para 3° lugar
          corBorda: '#CD7F32', // Bronze
          nomeCor: 'Bronze',
        };
      default:
        return {
          imagem: '/podiumdouradoo.png',
          corBorda: '#DAA520',
          nomeCor: 'douradooo',
        };
    }
  };

  const podiumConfig = useMemo(() => {
    if (!corretorData) return getPodiumConfig(1);
    return getPodiumConfig(corretorData.posicao);
  }, [corretorData]);

  const handleIniciarApresentacao = () => {
    // Reload custom photos when starting presentation
    setCustomPhotos(getCustomPhotos());
    setApresentacaoAtiva(true);
    setTelaAtual(1);
    setCorretorAtualIndex(0); // Start with 1° lugar (first in array)
    setTipoRankingAtual('agenciamentos'); // Start with agenciamentos
    setImagemProgressoErro(false);
  };

  const handleFecharApresentacao = () => {
    setApresentacaoAtiva(false);
    setTelaAtual(1);
    setCorretorAtualIndex(0);
    setTipoRankingAtual('agenciamentos');
    setImagemProgressoErro(false);
  };

  const handleProximo = () => {
    // Tela inicial (TOP) - só aparece uma vez no início de cada tipo de ranking
    if (telaAtual === 1 && corretorAtualIndex === 0) {
      // Primeira vez: vai para tela 2 (Valor) do primeiro corretor
      setTelaAtual(2);
    }
    // Se está na tela 2 (Valor)
    else if (telaAtual === 2) {
      // Avança para tela 3 (Resultado)
      setTelaAtual(3);
    }
    // Se está na tela 3 (Resultado)
    else if (telaAtual === 3) {
      // Avança para o próximo corretor (começa na tela 2 - Valor)
      if (corretorAtualIndex < top3Corretores.length - 1) {
        setCorretorAtualIndex(corretorAtualIndex + 1);
        setTelaAtual(2); // Próximo corretor começa na tela 2 (Valor)
        setImagemProgressoErro(false); // Reset erro de imagem
      } else {
        // Se já mostrou todos os corretores deste tipo, avança para próximo tipo de ranking
        if (tipoRankingAtual === 'agenciamentos') {
          // Próximo: Contratos
          setTipoRankingAtual('contratos');
          setTelaAtual(1);
          setCorretorAtualIndex(0);
          setImagemProgressoErro(false);
        } else if (tipoRankingAtual === 'contratos') {
          // Próximo: VGV
          setTipoRankingAtual('vgv');
          setTelaAtual(1);
          setCorretorAtualIndex(0);
          setImagemProgressoErro(false);
        } else {
          // Último tipo (VGV), vai para tela de agradecimento
          setTelaAtual(4);
        }
      }
    }
    // Se está na tela 4 (Agradecimento), não faz nada (última tela)
  };

  const handleVoltar = () => {
    // Se está na tela 4 (Agradecimento)
    if (telaAtual === 4) {
      // Volta para tela 3 (Resultado) do último corretor do último tipo (VGV)
      setTipoRankingAtual('vgv');
      setCorretorAtualIndex(top3Corretores.length - 1);
      setTelaAtual(3);
    }
    // Se está na tela 3 (Resultado)
    else if (telaAtual === 3) {
      // Volta para tela 2 (Valor) do mesmo corretor
      setTelaAtual(2);
    }
    // Se está na tela 2 (Valor)
    else if (telaAtual === 2) {
      // Se é o primeiro corretor, volta para tela inicial
      if (corretorAtualIndex === 0) {
        setTelaAtual(1);
      } else {
        // Volta para o corretor anterior (última tela - Resultado)
        setCorretorAtualIndex(corretorAtualIndex - 1);
        setTelaAtual(3);
        setImagemProgressoErro(false);
      }
    }
    // Se está na tela 1 (inicial)
    else if (telaAtual === 1) {
      // Se não é o primeiro tipo, volta para último corretor do tipo anterior
      if (tipoRankingAtual === 'vgv') {
        setTipoRankingAtual('contratos');
        setCorretorAtualIndex(top3Corretores.length - 1);
        setTelaAtual(3);
        setImagemProgressoErro(false);
      } else if (tipoRankingAtual === 'contratos') {
        setTipoRankingAtual('agenciamentos');
        setCorretorAtualIndex(top3Corretores.length - 1);
        setTelaAtual(3);
        setImagemProgressoErro(false);
      }
      // Se é agenciamentos (primeiro tipo), não faz nada
    }
  };

  // Check if we can go to next
  const podeAvancar = useMemo(() => {
    // Tela inicial: sempre pode avançar (vai para tela 2)
    if (telaAtual === 1 && corretorAtualIndex === 0) return true;
    // Tela 2: sempre pode avançar (vai para tela 3)
    if (telaAtual === 2) return true;
    // Tela 3: pode avançar se ainda há mais corretores ou se é o último (vai para tela 4)
    if (telaAtual === 3) return true;
    // Tela 4: não pode avançar (última tela)
    return false;
  }, [telaAtual, corretorAtualIndex, top3Corretores.length]);

  // Check if we can go back
  const podeVoltar = useMemo(() => {
    // Tela inicial do primeiro tipo (agenciamentos): não pode voltar
    if (telaAtual === 1 && corretorAtualIndex === 0 && tipoRankingAtual === 'agenciamentos') return false;
    // Tela 2, 3 ou 4: sempre pode voltar
    // Tela 1 de outros tipos: pode voltar
    return true;
  }, [telaAtual, corretorAtualIndex, tipoRankingAtual]);

  // Configuration Screen
  if (!apresentacaoAtiva) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          <Typography variant="h4" sx={{ mb: 4, fontWeight: 'bold' }}>
            Configuração de Apresentação
          </Typography>

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Ano</InputLabel>
                <Select
                  value={anoSelecionado}
                  onChange={(e) => setAnoSelecionado(e.target.value)}
                  label="Ano"
                >
                  {anos.map((ano) => (
                    <MenuItem key={ano} value={ano}>
                      {ano}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Período (Meses)</InputLabel>
                <Select
                  multiple
                  value={mesesSelecionados}
                  onChange={(e) => {
                    const value = e.target.value;
                    setMesesSelecionados(typeof value === 'string' ? value.split(',') : value);
                  }}
                  input={<OutlinedInput label="Período (Meses)" />}
                  renderValue={(selected) => {
                    if (selected.length === 0) return 'Nenhum mês selecionado';
                    if (selected.length === meses.length) return 'Todos os meses';
                    if (selected.length <= 3) return selected.join(', ');
                    return `${selected.length} meses selecionados`;
                  }}
                >
                  {meses.map((mes) => (
                    <MenuItem key={mes} value={mes}>
                      <Checkbox checked={mesesSelecionados.indexOf(mes) > -1} />
                      {mes}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {mesesSelecionados.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Período selecionado: {mesesSelecionados.length === meses.length 
                    ? 'Todos os meses' 
                    : mesesSelecionados.join(', ')} de {anoSelecionado}
                </Typography>
              </Grid>
            )}
          </Grid>

          {/* Custom Texts Configuration */}
          <Paper
            sx={{
              mt: 4,
              p: 3,
              background: `linear-gradient(135deg, ${colors.primary}20 0%, ${colors.primary}10 100%)`,
              border: `1px solid ${colors.primary}40`,
            }}
          >
            <Typography variant="h6" sx={{ mb: 3 }}>
              Configuração de Textos - Tela de Agradecimento
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Título Principal"
                  value={customTexts.tituloPrincipal}
                  onChange={(e) => {
                    const newTexts = { ...customTexts, tituloPrincipal: e.target.value };
                    saveCustomTexts(newTexts);
                  }}
                  placeholder="Ex: AGRADECEMOS!"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Subtítulo"
                  value={customTexts.subtitulo}
                  onChange={(e) => {
                    const newTexts = { ...customTexts, subtitulo: e.target.value };
                    saveCustomTexts(newTexts);
                  }}
                  placeholder="Ex: A todos os corretores"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Mensagem"
                  value={customTexts.mensagem}
                  onChange={(e) => {
                    const newTexts = { ...customTexts, mensagem: e.target.value };
                    saveCustomTexts(newTexts);
                  }}
                  placeholder="Ex: Pelo excelente trabalho e dedicação em 2025"
                  multiline
                  rows={2}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Pré-visualização - Top 3 Agenciamentos */}
          {top3Agenciamentos.length > 0 && (
            <Paper
              sx={{
                mt: 4,
                p: 3,
                background: `linear-gradient(135deg, ${colors.primary}20 0%, ${colors.primary}10 100%)`,
                border: `1px solid ${colors.primary}40`,
              }}
            >
              <Typography variant="h6" sx={{ mb: 3 }}>
                Pré-visualização - Top 3 Agenciamentos {mesesSelecionados.length === meses.length ? anoSelecionado : `${mesesSelecionados.join(', ')} ${anoSelecionado}`}
              </Typography>
              <Grid container spacing={3}>
                {top3Agenciamentos.map((corretor) => {
                  const config = getPodiumConfig(corretor.posicao);
                  return (
                    <Grid item xs={12} md={4} key={`agenciamentos-${corretor.id}`}>
                      <Paper
                        sx={{
                          p: 2,
                          background: `linear-gradient(135deg, ${config.corBorda}20 0%, ${config.corBorda}10 100%)`,
                          border: `2px solid ${config.corBorda}60`,
                          borderRadius: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              background: config.corBorda,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2,
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                color: '#ffffff',
                                fontWeight: 'bold',
                              }}
                            >
                              {corretor.posicao}°
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {corretor.nome}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Agenciamentos Total do Período
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: config.corBorda }}>
                          {corretor.agenciamentosAno.toLocaleString('pt-BR')}
                        </Typography>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          )}

          {/* Pré-visualização - Top 3 Contratos */}
          <Paper
            sx={{
              mt: 4,
              p: 3,
              background: `linear-gradient(135deg, ${colors.primary}20 0%, ${colors.primary}10 100%)`,
              border: `1px solid ${colors.primary}40`,
            }}
          >
            <Typography variant="h6" sx={{ mb: 3 }}>
              Pré-visualização - Top 3 Contratos {mesesSelecionados.length === meses.length ? anoSelecionado : `${mesesSelecionados.join(', ')} ${anoSelecionado}`}
            </Typography>
            {top3Contratos.length > 0 ? (
              <Grid container spacing={3}>
                {top3Contratos.map((corretor) => {
                  const config = getPodiumConfig(corretor.posicao);
                  return (
                    <Grid item xs={12} md={4} key={`contratos-${corretor.id}`}>
                      <Paper
                        sx={{
                          p: 2,
                          background: `linear-gradient(135deg, ${config.corBorda}20 0%, ${config.corBorda}10 100%)`,
                          border: `2px solid ${config.corBorda}60`,
                          borderRadius: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              background: config.corBorda,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2,
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                color: '#ffffff',
                                fontWeight: 'bold',
                              }}
                            >
                              {corretor.posicao}°
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {corretor.nome}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Contratos Total do Período
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: config.corBorda }}>
                          {corretor.contratosAno.toLocaleString('pt-BR')}
                        </Typography>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                Nenhum corretor com contratos cadastrados para o ano {anoSelecionado}
              </Typography>
            )}
          </Paper>

          {/* Pré-visualização - Top 3 VGV */}
          {top3VGV.length > 0 && (
            <Paper
              sx={{
                mt: 4,
                p: 3,
                background: `linear-gradient(135deg, ${colors.primary}20 0%, ${colors.primary}10 100%)`,
                border: `1px solid ${colors.primary}40`,
              }}
            >
              <Typography variant="h6" sx={{ mb: 3 }}>
                Pré-visualização - Top 3 VGV {mesesSelecionados.length === meses.length ? anoSelecionado : `${mesesSelecionados.join(', ')} ${anoSelecionado}`}
              </Typography>
              <Grid container spacing={3}>
                {top3VGV.map((corretor) => {
                  const config = getPodiumConfig(corretor.posicao);
                  return (
                    <Grid item xs={12} md={4} key={`vgv-${corretor.id}`}>
                      <Paper
                        sx={{
                          p: 2,
                          background: `linear-gradient(135deg, ${config.corBorda}20 0%, ${config.corBorda}10 100%)`,
                          border: `2px solid ${config.corBorda}60`,
                          borderRadius: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              background: config.corBorda,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              mr: 2,
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                color: '#ffffff',
                                fontWeight: 'bold',
                              }}
                            >
                              {corretor.posicao}°
                            </Typography>
                          </Box>
                        </Box>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                          {corretor.nome}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          VGV Total do Período
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 'bold', color: config.corBorda }}>
                          {formatarMoeda(corretor.vgvAno)}
                        </Typography>
                        
                        {/* Photo Upload Section */}
                        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${config.corBorda}40` }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            Foto para Apresentação
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              src={getCorretorPhoto(corretor) || undefined}
                              alt={corretor.nome}
                              sx={{
                                width: 60,
                                height: 60,
                                border: `2px solid ${config.corBorda}`,
                              }}
                            >
                              {!getCorretorPhoto(corretor) && corretor.nome
                                ? corretor.nome.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                                : ''}
                            </Avatar>
                            <Box>
                              <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id={`upload-photo-${corretor.id}`}
                                type="file"
                                onChange={(e) => handlePhotoUpload(corretor.id, e)}
                              />
                              <label htmlFor={`upload-photo-${corretor.id}`}>
                                <Button
                                  component="span"
                                  size="small"
                                  startIcon={<PhotoCameraIcon />}
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    background: config.corBorda,
                                    color: '#ffffff',
                                    '&:hover': {
                                      background: config.corBorda,
                                      opacity: 0.9,
                                    },
                                  }}
                                >
                                  {customPhotos[corretor.id] ? 'Alterar Foto' : 'Upload Foto'}
                                </Button>
                              </label>
                              {customPhotos[corretor.id] && (
                                <Button
                                  size="small"
                                  startIcon={<DeleteIcon />}
                                  onClick={() => handlePhotoRemove(corretor.id)}
                                  sx={{
                                    textTransform: 'none',
                                    fontSize: '0.75rem',
                                    ml: 1,
                                    color: 'error.main',
                                    '&:hover': {
                                      background: 'rgba(244, 67, 54, 0.1)',
                                    },
                                  }}
                                >
                                  Remover
                                </Button>
                              )}
                            </Box>
                          </Box>
                          {!customPhotos[corretor.id] && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              Usando foto de perfil
                            </Typography>
                          )}
                        </Box>
                      </Paper>
                    </Grid>
                  );
                })}
              </Grid>
            </Paper>
          )}

          {(top3Agenciamentos.length === 0 && top3Contratos.length === 0 && top3VGV.length === 0) && (
            <Paper
              sx={{
                mt: 4,
                p: 3,
                background: `linear-gradient(135deg, ${colors.primary}20 0%, ${colors.primary}10 100%)`,
                border: `1px solid ${colors.primary}40`,
                textAlign: 'center',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                Nenhum corretor com dados encontrado para o ano {anoSelecionado}
              </Typography>
            </Paper>
          )}

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              startIcon={<PlayArrowIcon />}
              onClick={handleIniciarApresentacao}
              disabled={(top3Agenciamentos.length === 0 && top3Contratos.length === 0 && top3VGV.length === 0) || mesesSelecionados.length === 0}
              sx={{
                px: 4,
                py: 1.5,
                fontSize: '1.1rem',
                background: colors.primary,
                '&:hover': {
                  background: colors.primary,
                  opacity: 0.9,
                },
                '&:disabled': {
                  background: 'rgba(255, 255, 255, 0.1)',
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              Iniciar Apresentação
            </Button>
          </Box>
        </Box>
      </Container>
    );
  }

  // Presentation Screen (Fullscreen) - Outside panel
  const presentationContent = (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        background: "#000000",
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
      }}
    >
      {/* Close Button */}
      <IconButton
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleFecharApresentacao();
        }}
        onMouseDown={(e) => {
          e.preventDefault();
          e.stopPropagation();
        }}
        sx={{
          position: 'fixed',
          top: 20,
          right: 20,
          color: '#ffffff',
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 10000,
          pointerEvents: 'auto',
          cursor: 'pointer',
          '&:hover': {
            background: 'rgba(0, 0, 0, 0.7)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>

      {/* Tela 1: Tela Inicial (Top VGV) - Apenas uma vez no início */}
      {telaAtual === 1 && corretorAtualIndex === 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            width: '100%',
            
          }}
        >
          {/* Golden Circle with Progress Image */}
          <Box
            sx={{
              width: { xs: '300px', md: '400px' },
              height: { xs: '300px', md: '400px' },
              mb: -22,
              borderRadius: '50%',
              border: '20px solid #DAA520',
              padding: 0,
              overflow: 'hidden',
              boxShadow: '0 0 30px rgba(230, 185, 73, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
            }}
          >
            <Box
              component="img"
              src="/corretor.jpg"
              alt="Progresso"
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              onError={(e) => {
                console.error('Erro ao carregar imagem do círculo de progresso');
              }}
            />
          </Box>

          {/* Golden Podium Image */}
          <Box
            component="img"
            src="/podiumdouradoo.png"
            alt="Pódiodouradoo"
            sx={{
              maxWidth: '600px',
              width: '100%',
              mb: '-40px',
              
            }}
            onError={(e) => {
              console.error('Erro ao carregar imagem do pódio');
            }}
          />

          {/* Top Title Text */}
          <Typography
            variant="h2"
            sx={{
              color: '#ffffff',
              fontWeight: 'bold',
              mb: -1,
              textTransform: 'uppercase',
              letterSpacing: 2,
              fontSize: { xs: '2.5rem', md: '4rem' },
            }}
          >
            {getTituloRanking()}
          </Typography>

          {/* Period Text */}
          <Typography
            variant="h4"
            sx={{
              color: '#ffffff',
              fontWeight: 400,
              textTransform: 'uppercase',
              letterSpacing: 1.5,
              fontSize: { xs: '1.5rem', md: '2rem' },
            }}
          >
            {mesesSelecionados.length === meses.length 
              ? `ANO DE ${anoSelecionado}`
              : `${mesesSelecionados.join(' / ').toUpperCase()} ${anoSelecionado}`}
          </Typography>

          {/* Back Button */}
          <Button
            onClick={handleVoltar}
            disabled={!podeVoltar}
            sx={{
              position: 'fixed',
              bottom: 30,
              left: 30,
              minWidth: 'auto',
              width: 50,
              height: 50,
              borderRadius: '50%',
              background: podeVoltar ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
              color: podeVoltar ? '#ffffff' : 'rgba(255, 255, 255, 0.3)',
              border: podeVoltar ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
              cursor: podeVoltar ? 'pointer' : 'not-allowed',
              zIndex: 10000,
              '&:hover': {
                background: podeVoltar ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: podeVoltar ? '1px solid rgba(255, 255, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'rgba(255, 255, 255, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            ←
          </Button>

          {/* Next Button */}
          <Button
            onClick={handleProximo}
            disabled={!podeAvancar}
            sx={{
              position: 'fixed',
              bottom: 30,
              right: 30,
              minWidth: 'auto',
              width: 50,
              height: 50,
              borderRadius: '50%',
              background: podeAvancar ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
              color: podeAvancar ? '#ffffff' : 'rgba(255, 255, 255, 0.3)',
              border: podeAvancar ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
              cursor: podeAvancar ? 'pointer' : 'not-allowed',
              '&:hover': {
                background: podeAvancar ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: podeAvancar ? '1px solid rgba(255, 255, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'rgba(255, 255, 255, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              },
              zIndex: 10000,
            }}
          >
            →
          </Button>
        </Box>
      )}

      {/* Tela 2: Apenas Valor */}
      {telaAtual === 2 && (
        <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {/* Top Left - Valor */}
          <Typography
            variant="h2"
            sx={{
              position: 'absolute',
              top: 40,
              left: 60,
              color: '#ffffff',
              fontWeight: 'bold',
              fontSize: { xs: '2rem', md: '3.5rem' },
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
            }}
          >
            {formatarValorRanking(getValorExibicao(corretorData))}
          </Typography>

          {/* Top Right - Top Title */}
          <Typography
            variant="h5"
            sx={{
              position: 'absolute',
              top: 40,
              right: 60,
              color: '#ffffff',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 1,
              fontSize: { xs: '1.2rem', md: '1.5rem' },
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
            }}
          >
            {getTituloRanking()}
          </Typography>

          {/* Back Button */}
          <Button
            onClick={handleVoltar}
            disabled={!podeVoltar}
            sx={{
              position: 'fixed',
              bottom: 30,
              left: 30,
              minWidth: 'auto',
              width: 50,
              height: 50,
              borderRadius: '50%',
              background: podeVoltar ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
              color: podeVoltar ? '#ffffff' : 'rgba(255, 255, 255, 0.3)',
              border: podeVoltar ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
              cursor: podeVoltar ? 'pointer' : 'not-allowed',
              zIndex: 10000,
              '&:hover': {
                background: podeVoltar ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: podeVoltar ? '1px solid rgba(255, 255, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'rgba(255, 255, 255, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            ←
          </Button>

          {/* Next Button */}
          <Button
            onClick={handleProximo}
            disabled={!podeAvancar}
            sx={{
              position: 'fixed',
              bottom: 30,
              right: 30,
              minWidth: 'auto',
              width: 50,
              height: 50,
              borderRadius: '50%',
              background: podeAvancar ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
              color: podeAvancar ? '#ffffff' : 'rgba(255, 255, 255, 0.3)',
              border: podeAvancar ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
              cursor: podeAvancar ? 'pointer' : 'not-allowed',
              '&:hover': {
                background: podeAvancar ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: podeAvancar ? '1px solid rgba(255, 255, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'rgba(255, 255, 255, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              },
              zIndex: 10000,
            }}
          >
            →
          </Button>
        </Box>
      )}

      {/* Tela 3: Pódio e Corretor */}
      {telaAtual === 3 && (
        <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
          {/* Top Left - Valor */}
          <Typography
            variant="h2"
            sx={{
              position: 'absolute',
              top: 40,
              left: 60,
              color: '#ffffff',
              fontWeight: 'bold',
              fontSize: { xs: '2rem', md: '3.5rem' },
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
              zIndex: 10,
            }}
          >
            {formatarValorRanking(getValorExibicao(corretorData))}
          </Typography>

          {/* Top Right - Top Title */}
          <Typography
            variant="h5"
            sx={{
              position: 'absolute',
              top: 40,
              right: 60,
              color: '#ffffff',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 1,
              fontSize: { xs: '1.2rem', md: '1.5rem' },
              textShadow: '2px 2px 4px rgba(0, 0, 0, 0.7)',
              zIndex: 10,
            }}
          >
            {getTituloRanking()} - {mesesSelecionados.length === meses.length 
              ? anoSelecionado 
              : `${mesesSelecionados.join(' / ')} ${anoSelecionado}`}
          </Typography>

          <Grid container sx={{ height: '100%', width: '100%', px: 8, py: 4, }}>
            {/* Left Side - Circle with progress image + Podium */}
            <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                  gap: 0,
                }}
              >
                {/* Circle with progress image */}
                <Box
                  sx={{
                    width: { xs: 250, md: 300 },
                    height: { xs: 250, md: 300 },
                    borderRadius: '50%',
                    border: `20px solid ${podiumConfig.corBorda}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: -20,
                    position: 'relative',
                    overflow: 'hidden',
                    boxShadow: `0 0 30px ${podiumConfig.corBorda}80`,
                  }}
                >
                  {!imagemProgressoErro && (
                    <Box
                      component="img"
                      src="/corretor.jpg"
                      alt="Progresso"
                      onError={() => setImagemProgressoErro(true)}
                      sx={{
                        width: '100%',
                        contentFit: 'cover',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '50%',
                        
                      }}
                    />
                  )}
                </Box>
              

                {/* Podium Image */}
                <Box
                  component="img"
                  src={podiumConfig.imagem}
                  alt="Pódio"
                  sx={{
                    maxWidth: { xs: '200px', md: '500px' },
                    width: '100%',
                    height: 'auto',
                    mt: 2,
                    mb: '-40px',


                  }}
                  onError={(e) => {
                    console.error('Erro ao carregar imagem do pódio');
                  }}
                />

                {/* Podium with position, name and year */}
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    zIndex: 5,
                    
                  }}
                >
                   <Typography
                    sx={{
                      color: '#ffffff',
                      fontWeight: '300',
                      textShadow: '2px 2px 4px rgb(0, 0, 0)',
                      fontSize: { xs: '1.2rem', md: '1.5rem' },
                      fontStyle: 'italic',
                      mb: "-22px",
                    }}
                  >
                    {corretorData?.posicao}° LUGAR
                  </Typography>
                  <Typography
                    sx={{
                      color: '#ffffff',
                      fontWeight: 'bold',
                      textShadow: '2px 2px 4px rgb(0, 0, 0)',
                      fontSize: { xs: '1.5rem', md: '50px' },
                      mb: "-22px",
                    }}
                  >
                    {corretorData?.nome}
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{
                        color: '#ffffff',
                        fontWeight: '300',
                        textShadow: '2px 2px 4px rgb(0, 0, 0)',
                        fontSize: { xs: '1.2rem', md: '1.5rem' },
                        fontStyle: 'italic',
                     
                    }}
                  >
                    {mesesSelecionados.length === meses.length 
                      ? `ANO DE ${anoSelecionado}`
                      : `${mesesSelecionados.join(' / ').toUpperCase()} ${anoSelecionado}`}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            {/* Right Side - Photo */}
            <Grid item xs={12} md={6} sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'center', p: 0, height: '100vh', minHeight: '100vh', position: 'relative' }}>
              {corretorData?.fotoApresentacao ? (
                // Custom photo - show in real size (large) - aligned to bottom
                <Box
                  component="img"
                  src={corretorData.fotoApresentacao}
                  alt={corretorData.nome}
                  sx={{
                    width: 'auto',
                    height: '80vh',
                    maxWidth: '80vw',
                    objectFit: 'contain',
                    borderRadius: 2,
                    margin: '0 auto',
                    position: 'absolute',
                    bottom: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                  }}
                />
              ) : (
                // Default - circular Avatar
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                    height: '100%',
                  }}
                >
                  <Avatar
                    src={corretorData?.foto || undefined}
                    alt={corretorData?.nome}
                    sx={{
                      width: { xs: 300, md: 500 },
                      height: { xs: 300, md: 500 },
                      border: `8px solid ${colors.primary}`,
                      boxShadow: `0 0 40px ${colors.primary}80`,
                    }}
                  >
                    {!corretorData?.foto && corretorData?.nome
                      ? corretorData.nome.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()
                      : ''}
                  </Avatar>
                </Box>
              )}
            </Grid>
          </Grid>

          {/* Back Button */}
          <Button
            onClick={handleVoltar}
            disabled={!podeVoltar}
            sx={{
              position: 'fixed',
              bottom: 30,
              left: 30,
              minWidth: 'auto',
              width: 50,
              height: 50,
              borderRadius: '50%',
              background: podeVoltar ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
              color: podeVoltar ? '#ffffff' : 'rgba(255, 255, 255, 0.3)',
              border: podeVoltar ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
              cursor: podeVoltar ? 'pointer' : 'not-allowed',
              zIndex: 10000,
              '&:hover': {
                background: podeVoltar ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: podeVoltar ? '1px solid rgba(255, 255, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'rgba(255, 255, 255, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            ←
          </Button>

          {/* Next Button - Always visible in all screens */}
          <Button
            onClick={handleProximo}
            disabled={!podeAvancar}
            sx={{
              position: 'fixed',
              bottom: 30,
              right: 30,
              minWidth: 'auto',
              width: 50,
              height: 50,
              borderRadius: '50%',
              background: podeAvancar ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
              color: podeAvancar ? '#ffffff' : 'rgba(255, 255, 255, 0.3)',
              border: podeAvancar ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
              cursor: podeAvancar ? 'pointer' : 'not-allowed',
              zIndex: 10000,
              '&:hover': {
                background: podeAvancar ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: podeAvancar ? '1px solid rgba(255, 255, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'rgba(255, 255, 255, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            →
          </Button>
        </Box>
      )}

      {/* Tela 4: Agradecimento */}
      {telaAtual === 4 && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: "#000000",
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            padding: 4,
            overflow: 'hidden',
          }}
        >
          {/* Confetes Animation */}
          <style>
            {`
              @keyframes confettiFall {
                0% {
                  transform: translateY(-100vh) rotate(0deg);
                  opacity: 1;
                }
                100% {
                  transform: translateY(100vh) rotate(720deg);
                  opacity: 0;
                }
              }
            `}
          </style>
          
          {/* Confetes - Círculos */}
          {Array.from({ length: 50 }).map((_, index) => {
            const colors = ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#FF1493'];
            const color = colors[index % colors.length];
            const left = `${(index * 2) % 100}%`;
            const delay = (index * 0.1) % 3;
            const duration = 5 + (index % 4); // Aumentado de 3-6 para 5-9 segundos
            
            return (
              <Box
                key={`circle-${index}`}
                sx={{
                  position: 'absolute',
                  left: left,
                  top: '-10px',
                  width: '10px',
                  height: '10px',
                  backgroundColor: color,
                  borderRadius: '50%',
                  animation: `confettiFall ${duration}s linear ${delay}s infinite`,
                }}
              />
            );
          })}
          
          {/* Confetes - Retângulos */}
          {Array.from({ length: 30 }).map((_, index) => {
            const colors = ['#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#FF1493'];
            const color = colors[index % colors.length];
            const left = `${((index + 50) * 3) % 100}%`;
            const delay = ((index + 50) * 0.1) % 3;
            const duration = 5 + ((index + 50) % 4); // Aumentado de 3-6 para 5-9 segundos
            const width = 8 + (index % 5) * 2;
            const height = 8 + (index % 5) * 2;
            
            return (
              <Box
                key={`rect-${index}`}
                sx={{
                  position: 'absolute',
                  left: left,
                  top: '-10px',
                  width: `${width}px`,
                  height: `${height}px`,
                  backgroundColor: color,
                  animation: `confettiFall ${duration}s linear ${delay}s infinite`,
                }}
              />
            );
          })}

          {/* Close Button */}
          <IconButton
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleFecharApresentacao();
            }}
            sx={{
              position: 'absolute',
              top: 20,
              right: 20,
              color: '#ffffff',
              background: 'rgba(0, 0, 0, 0.3)',
              zIndex: 10001,
              '&:hover': {
                background: 'rgba(0, 0, 0, 0.5)',
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Prime Logo */}
          <Box
            component="img"
            src="/logobranca.png"
            alt="Prime Logo"
            sx={{
              maxWidth: { xs: '200px', md: '500px' },
              width: '100%',
              height: 'auto',
              mb: 6,
              filter: 'drop-shadow(2px 2px 8px rgba(0, 0, 0, 0.5))',
            }}
            onError={(e) => {
              // Fallback para logoprime.webp se logobranca.png não existir
              e.target.src = '/logoprime.webp';
            }}
          />

          {/* Thank You Message */}
          <Typography
            variant="h1"
            sx={{
              color: '#ffffff',
              fontWeight: 'bold',
              fontSize: { xs: '3rem', md: '5rem' },
              textAlign: 'center',
              mb: 4,
              textShadow: '2px 2px 8px rgba(0, 0, 0, 0.5)',
            }}
          >
            {customTexts.tituloPrincipal}
          </Typography>

          <Typography
            variant="h4"
            sx={{
              color: '#ffffff',
              fontWeight: 400,
              fontSize: { xs: '1.5rem', md: '2.5rem' },
              textAlign: 'center',
              mb: 2,
              textShadow: '2px 2px 6px rgba(0, 0, 0, 0.5)',
            }}
          >
            {customTexts.subtitulo}
          </Typography>

          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: 300,
              fontSize: { xs: '1.2rem', md: '1.8rem' },
              textAlign: 'center',
              maxWidth: '800px',
              textShadow: '1px 1px 4px rgba(0, 0, 0, 0.5)',
            }}
          >
            {customTexts.mensagem}
          </Typography>

          {/* Back Button */}
          <Button
            onClick={handleVoltar}
            disabled={!podeVoltar}
            sx={{
              position: 'fixed',
              bottom: 30,
              left: 30,
              minWidth: 'auto',
              width: 50,
              height: 50,
              borderRadius: '50%',
              background: podeVoltar ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.05)',
              color: podeVoltar ? '#ffffff' : 'rgba(255, 255, 255, 0.3)',
              border: podeVoltar ? '1px solid rgba(255, 255, 255, 0.3)' : '1px solid rgba(255, 255, 255, 0.1)',
              cursor: podeVoltar ? 'pointer' : 'not-allowed',
              zIndex: 10000,
              '&:hover': {
                background: podeVoltar ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.05)',
                border: podeVoltar ? '1px solid rgba(255, 255, 255, 0.5)' : '1px solid rgba(255, 255, 255, 0.1)',
              },
              '&:disabled': {
                background: 'rgba(255, 255, 255, 0.05)',
                color: 'rgba(255, 255, 255, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            ←
          </Button>
        </Box>
      )}
    </Box>
  );

  // Render presentation outside panel using portal
  return createPortal(presentationContent, document.body);
}

export default Apresentacao;

